import { useState, type FormEvent } from 'react';
import { apiFetch } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import { useToast } from '../components/Toast.js';
import { Button, Input } from '../components/ui/index.js';

export function ProfileSection() {
  const { userEmail } = useAuth();
  const { toast } = useToast();

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-gray-900">Profile</h2>

      {/* Email (read-only) */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <h3 className="text-sm font-medium text-gray-700">Account</h3>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
          <p className="text-sm text-gray-900 bg-gray-50 rounded-lg px-3 py-2.5">{userEmail ?? '--'}</p>
        </div>
      </div>

      <NameSection toast={toast} />
      <ChangePasswordSection toast={toast} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Name (First + Last)                                                */
/* ------------------------------------------------------------------ */

function NameSection({ toast }: { toast: (type: 'success' | 'error', msg: string) => void }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() && !lastName.trim()) return;
    setIsSaving(true);
    try {
      await apiFetch('/users/me', {
        method: 'PUT',
        body: JSON.stringify({
          first_name: firstName.trim() || undefined,
          last_name: lastName.trim() || undefined,
        }),
      });
      toast('success', 'Name updated');
    } catch {
      toast('error', 'Failed to update name');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
      <h3 className="text-sm font-medium text-gray-700">Name</h3>
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="First Name"
          type="text"
          value={firstName}
          onChange={e => setFirstName(e.target.value)}
          placeholder="First name"
          autoComplete="given-name"
        />
        <Input
          label="Last Name"
          type="text"
          value={lastName}
          onChange={e => setLastName(e.target.value)}
          placeholder="Last name"
          autoComplete="family-name"
        />
      </div>
      <Button
        variant="primary"
        size="md"
        loading={isSaving}
        disabled={!firstName.trim() && !lastName.trim()}
        type="submit"
      >
        {isSaving ? 'Saving...' : 'Save Name'}
      </Button>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/*  Change Password                                                    */
/* ------------------------------------------------------------------ */

function ChangePasswordSection({ toast }: { toast: (type: 'success' | 'error', msg: string) => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < 12) {
      setError('New password must be at least 12 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setIsSaving(true);
    try {
      await apiFetch('/users/me/password', {
        method: 'PUT',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });
      toast('success', 'Password updated');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      toast('error', 'Failed to update password');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
      <h3 className="text-sm font-medium text-gray-700">Change Password</h3>

      {error && (
        <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-100">
          {error}
        </div>
      )}

      <Input
        label="Current Password"
        type="password"
        value={currentPassword}
        onChange={e => setCurrentPassword(e.target.value)}
        required
        autoComplete="current-password"
      />

      <Input
        label="New Password"
        type="password"
        value={newPassword}
        onChange={e => setNewPassword(e.target.value)}
        required
        minLength={12}
        autoComplete="new-password"
        placeholder="Min 12 characters"
      />

      <Input
        label="Confirm New Password"
        type="password"
        value={confirmPassword}
        onChange={e => setConfirmPassword(e.target.value)}
        required
        minLength={12}
        autoComplete="new-password"
      />

      <Button
        variant="primary"
        size="md"
        loading={isSaving}
        disabled={!currentPassword || !newPassword || !confirmPassword}
        type="submit"
      >
        {isSaving ? 'Updating...' : 'Update Password'}
      </Button>
    </form>
  );
}
